import React from 'react';
import TaskDetailsTabContent from './tabs/TaskDetails';
import TaskAttachmentsTabContent from './tabs/TaskAttachments';
import FeedComponent from '../Management/Shared/FeedComponent';

interface TaskDetailsContentProps {
  activeTab: string;
  taskDetails: any;
  taskUuid: string;
  userAccessLevel: 'edit' | 'view' | 'owner' | 'admin';
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  formik: any;
  hasChanges: boolean;
  setHasChanges: (changes: boolean) => void;
  onSubmit: () => void;
  taskStatus: string;
  comments: any[];
  setComments: React.Dispatch<React.SetStateAction<any[]>>;
  collaborators: any[];
  setCollaborators: React.Dispatch<React.SetStateAction<any[]>>;
  attachments: any[];
  setAttachments: React.Dispatch<React.SetStateAction<any[]>>;
  matterData: any;
  setMatterData: React.Dispatch<React.SetStateAction<any>>;
  commentsLoading: boolean;
  collaboratorsLoading: boolean;
  attachmentsLoading: boolean;
  matterLoading: boolean;
  newComment: string;
  setNewComment: React.Dispatch<React.SetStateAction<string>>;
  selectedCaseId: string;
  setSelectedCaseId: React.Dispatch<React.SetStateAction<string>>;
  matters: any[];
  refetchTabData: (tab: string) => Promise<void>;
  handleAddComment: () => Promise<void>;
  handleOpenUploadModal: () => void;
  handleFileUpload?: (files: File[]) => Promise<void> | void;
  handleOpenAttachment: (attachment: any) => Promise<void>;
  handleDeleteCollaborator: (collaboratorUuid: string) => void;
  handleDeleteAttachment: (attachmentId: string) => void;
  handleChangeMatter: () => Promise<void>;
  onOpenAuditLog?: () => void;
  // Feed props
  taskFeed?: any[];
  taskFeedLoading?: boolean;
  onRefreshFeed?: () => Promise<void>;
  onPostFeedEvent?: (eventData: { event_type: string; message: string; metadata?: any }) => Promise<void>;
  onFeedCommentAdd?: (eventId: string, comment: string) => Promise<void>;
  currentUser?: { id: string; first_name: string; last_name: string; email: string };
  feedMembers?: Array<{ id: string; first_name: string; last_name: string; email: string }>;
}

const TaskDetailsContent: React.FC<TaskDetailsContentProps> = ({
  activeTab,
  taskDetails,
  taskUuid,
  userAccessLevel,
  isEditing,
  setIsEditing,
  formik,
  hasChanges,
  setHasChanges,
  onSubmit,
  taskStatus,
  comments,
  setComments,
  collaborators,
  setCollaborators,
  attachments,
  setAttachments,
  matterData,
  setMatterData,
  commentsLoading,
  collaboratorsLoading,
  attachmentsLoading,
  matterLoading,
  newComment,
  setNewComment,
  selectedCaseId,
  setSelectedCaseId,
  matters,
  refetchTabData,
  handleAddComment,
  handleOpenUploadModal,
  handleFileUpload,
  handleOpenAttachment,
  handleDeleteCollaborator,
  handleDeleteAttachment,
  handleChangeMatter,
  onOpenAuditLog,
  taskFeed = [],
  taskFeedLoading = false,
  onRefreshFeed,
  onPostFeedEvent,
  onFeedCommentAdd,
  currentUser,
  feedMembers = [],
}) => {
  const canPost = userAccessLevel !== 'view' && !['completed', 'deleted'].includes(taskStatus);

  const renderContent = () => {
    switch (activeTab) {
      case 'Details':
        return (
          <TaskDetailsTabContent
            taskDetails={taskDetails}
            taskUuid={taskUuid}
            userAccessLevel={userAccessLevel}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            formik={formik}
            hasChanges={hasChanges}
            setHasChanges={setHasChanges}
            onSubmit={onSubmit}
            taskStatus={taskStatus}
            matterData={matterData}
            setMatterData={setMatterData}
            matterLoading={matterLoading}
            selectedCaseId={selectedCaseId}
            setSelectedCaseId={setSelectedCaseId}
            matters={matters}
            handleChangeMatter={handleChangeMatter}
            onOpenAuditLog={onOpenAuditLog}
          />
        );
      case 'Feed':
        return (
          <div className="flex-1 min-h-0 flex flex-col">
            <FeedComponent
              events={taskFeed}
              loading={taskFeedLoading}
              onRefresh={onRefreshFeed || (async () => {})}
              onPostEvent={canPost ? onPostFeedEvent : undefined}
              onCommentAdd={canPost ? onFeedCommentAdd : undefined}
              currentUser={currentUser}
              teamMembers={feedMembers}
            />
          </div>
        );
      case 'Attachments':
        return (
          <TaskAttachmentsTabContent
            taskDetails={taskDetails}
            taskUuid={taskUuid}
            userAccessLevel={userAccessLevel}
            taskStatus={taskStatus}
            attachments={attachments}
            setAttachments={setAttachments}
            attachmentsLoading={attachmentsLoading}
            handleOpenUploadModal={handleOpenUploadModal}
            handleFileUpload={handleFileUpload}
            handleOpenAttachment={handleOpenAttachment}
            handleDeleteAttachment={handleDeleteAttachment}
          />
        );
      default:
        return null;
    }
  };

  return renderContent();
};

export default TaskDetailsContent;
